/**
 * The rate-limit modal — one explanation, wherever the limit is hit.
 *
 * A rate limit used to arrive as whatever the calling code happened to render:
 * Google's raw JSON error envelope printed into a results row, an error document
 * saved as the day's brief, or — during a scheduled run — nothing at all. This
 * is the single place that says it, raised from three directions:
 *
 *   - `lib/api.ts`, for any 429 with the shared envelope;
 *   - `pages/dashboard.ts`, for the PDF download, which bypasses `api()`;
 *   - `pages/signal_management.ts`, for a `[rate-limit-event]` line arriving on
 *     the collection log stream, where there is no HTTP request to carry a 429.
 *
 * Module singleton, like `search_panel.ts`: a second event updates the modal
 * that is already open rather than stacking another one on top. A scan makes two
 * calls per article, so "a second event" is not an edge case — it is what the
 * next five seconds look like.
 *
 * Imports nothing from `lib/api`. `api()` raises this modal, so the reverse
 * import would be a cycle.
 */

/** The envelope built by `backend/rate_limit.describe()`. Every field optional:
 *  it also arrives from a log line, and a modal that fails to open because one
 *  key was missing helps nobody. */
export interface RateLimitInfo {
  error?: string;
  provider?: string | null;
  model?: string | null;
  retryAfterSeconds?: number | null;
  resetAt?: string | null;
  configuredRpm?: number | null;
  message?: string | null;
}

const PROVIDER_LABELS: Record<string, string> = {
  google: "Google AI",
  anthropic: "Anthropic",
};

let _overlay: HTMLElement | null = null;
let _countdownTimer: number | null = null;
/** Wall-clock deadline, not a decrementing counter — a background tab throttles
 *  setInterval to once a minute and a counter would drift into fiction. */
let _deadline = 0;
let _onKeydown: ((e: KeyboardEvent) => void) | null = null;

/** True when the envelope looks like ours. Used by callers deciding whether a
 *  429 body is a rate limit they can explain or an unfamiliar one to re-throw. */
export function isRateLimitInfo(value: unknown): value is RateLimitInfo {
  return !!value && typeof value === "object" && (value as RateLimitInfo).error === "rate_limited";
}

function providerLabel(info: RateLimitInfo): string {
  const p = info.provider ?? "";
  return PROVIDER_LABELS[p] ?? (p || "The analysis provider");
}

/** One line for a log or an inline notice — the same facts without the modal. */
export function rateLimitOneLiner(info: RateLimitInfo): string {
  const parts = [`${providerLabel(info)} is rate limiting requests`];
  if (info.model) parts.push(`on ${info.model}`);
  const wait = info.retryAfterSeconds;
  parts.push(
    typeof wait === "number" && wait > 0
      ? `— retry in about ${Math.ceil(wait)}s`
      : "— retry shortly"
  );
  return parts.join(" ");
}

export function showRateLimitModal(info: RateLimitInfo): void {
  if (_overlay) {
    updateModal(info);
    return;
  }

  _overlay = document.createElement("div");
  _overlay.className = "modal-overlay rl-overlay";
  _overlay.setAttribute("role", "dialog");
  _overlay.setAttribute("aria-modal", "true");
  _overlay.setAttribute("aria-label", "Rate limited");
  _overlay.innerHTML = `
    <div class="modal-box rl-box">
      <div class="modal-header">
        <h3>Too many requests</h3>
        <button class="icon-btn modal-close" aria-label="Close">✕</button>
      </div>
      <div class="modal-body">
        <p class="rl-lead"></p>
        <p class="rl-countdown" role="status" aria-live="polite"></p>
        <p class="rl-advice muted"></p>
        <details class="rl-detail">
          <summary>What the provider said</summary>
          <p class="rl-message"></p>
        </details>
      </div>
      <div class="modal-footer">
        <button class="primary rl-dismiss">Dismiss</button>
      </div>
    </div>`;

  // Same three ways out as every other modal here: the ✕, the scrim, Escape.
  _overlay.querySelector(".modal-close")!.addEventListener("click", closeRateLimitModal);
  _overlay.querySelector(".rl-dismiss")!.addEventListener("click", closeRateLimitModal);
  _overlay.addEventListener("click", (e) => {
    if (e.target === _overlay) closeRateLimitModal();
  });
  _onKeydown = (e: KeyboardEvent) => {
    if (e.key === "Escape") closeRateLimitModal();
  };
  document.addEventListener("keydown", _onKeydown);

  document.body.appendChild(_overlay);
  updateModal(info);
}

export function closeRateLimitModal(): void {
  if (_countdownTimer !== null) {
    window.clearInterval(_countdownTimer);
    _countdownTimer = null;
  }
  if (_onKeydown) {
    document.removeEventListener("keydown", _onKeydown);
    _onKeydown = null;
  }
  _overlay?.remove();
  _overlay = null;
}

function updateModal(info: RateLimitInfo): void {
  if (!_overlay) return;
  const q = (sel: string) => _overlay!.querySelector(sel) as HTMLElement;

  const rpm = info.configuredRpm;
  q(".rl-lead").textContent =
    `${providerLabel(info)} is refusing requests for the moment` +
    (info.model ? ` on ${info.model}.` : ".") +
    " Nothing was lost — whatever was being analysed stays uncollected and will be picked up on the next run.";

  q(".rl-advice").textContent =
    typeof rpm === "number" && rpm > 0
      ? `Requests are paced at ${rpm} per minute (Admin Panel → App Settings → Provider rate limits). ` +
        "If the key allows more, raise it there; if this keeps happening, lower it."
      : "Pacing is switched off for this provider, so every call goes straight to it. " +
        "Set a requests-per-minute limit in Admin Panel → App Settings → Provider rate limits.";

  const message = q(".rl-message");
  const detail = q(".rl-detail");
  message.textContent = info.message ?? "";
  detail.style.display = info.message ? "" : "none";

  // Restart the countdown on every event: a later refusal moves the deadline,
  // and a stale timer counting to a moment that has already passed is worse
  // than none.
  if (_countdownTimer !== null) {
    window.clearInterval(_countdownTimer);
    _countdownTimer = null;
  }
  const wait = info.retryAfterSeconds;
  if (typeof wait === "number" && wait > 0) {
    _deadline = Date.now() + wait * 1000;
    tickCountdown();
    _countdownTimer = window.setInterval(tickCountdown, 1000);
  } else {
    // No retry delay was sent. Say so plainly rather than inventing a timer:
    // the provider-429 path can reach here when the error carried no RetryInfo.
    q(".rl-countdown").textContent = "Try again shortly.";
  }
}

function tickCountdown(): void {
  if (!_overlay) return;
  const el = _overlay.querySelector(".rl-countdown") as HTMLElement;
  const left = Math.ceil((_deadline - Date.now()) / 1000);
  if (left <= 0) {
    el.textContent = "You can retry now.";
    el.classList.add("rl-countdown-ready");
    if (_countdownTimer !== null) {
      window.clearInterval(_countdownTimer);
      _countdownTimer = null;
    }
    return;
  }
  el.classList.remove("rl-countdown-ready");
  el.textContent = left >= 60
    ? `Try again in ${Math.floor(left / 60)}m ${left % 60}s.`
    : `Try again in ${left}s.`;
}
